import { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, UserCog, MapPin, Phone, Mail, X, Search, Key, Lock, Unlock, Eye, EyeOff, Copy, User, Shield, RefreshCw, CheckCircle } from 'lucide-react';
import api from '@/utils/api';
import SEO from '@/components/SEO';

const ExecutiveManagement = () => {
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingExecutive, setEditingExecutive] = useState(null);
  const [allocatedDistricts, setAllocatedDistricts] = useState([]);
  const [districtSearch, setDistrictSearch] = useState('');
  const [districtSuggestions, setDistrictSuggestions] = useState([]);
  const [showDistrictSuggestions, setShowDistrictSuggestions] = useState(false);
  const districtInputRef = useRef(null);
  
  // Credentials management
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [credentialsTarget, setCredentialsTarget] = useState(null);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [generatedPasscode, setGeneratedPasscode] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    allocated_districts: []
  });

  useEffect(() => {
    fetchExecutives();
    fetchAllocatedDistricts();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (districtInputRef.current && !districtInputRef.current.contains(e.target)) {
        setShowDistrictSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchExecutives = async () => {
    try {
      const response = await api.get('/admin/executives');
      setExecutives(response.data);
    } catch (error) {
      toast.error('Failed to fetch executives');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllocatedDistricts = async () => {
    try {
      const response = await api.get('/admin/executives/allocated-districts');
      setAllocatedDistricts(response.data);
    } catch (error) {
      console.error('Failed to fetch allocated districts');
    }
  };

  const searchDistricts = async (query) => {
    if (query.length < 2) {
      setDistrictSuggestions([]);
      return;
    }
    try {
      const response = await api.get(`/location-master/districts/search?q=${encodeURIComponent(query)}&limit=15`);
      // Filter out already allocated districts (except to current executive)
      const filtered = response.data.filter(d => {
        const isAllocated = allocatedDistricts.some(
          a => a.state === d.state && a.district === d.district && 
          (!editingExecutive || a.allocated_to !== editingExecutive.name)
        );
        const isSelected = formData.allocated_districts.some(
          s => s.state === d.state && s.district === d.district
        );
        return !isAllocated && !isSelected;
      });
      setDistrictSuggestions(filtered);
    } catch (error) {
      console.error('Search failed');
    }
  };

  const handleDistrictSearchChange = (e) => {
    const value = e.target.value;
    setDistrictSearch(value);
    searchDistricts(value);
    setShowDistrictSuggestions(true);
  };

  const addDistrict = (district) => {
    if (!formData.allocated_districts.some(d => d.state === district.state && d.district === district.district)) {
      setFormData({
        ...formData,
        allocated_districts: [...formData.allocated_districts, { state: district.state, district: district.district }]
      });
    }
    setDistrictSearch('');
    setDistrictSuggestions([]);
    setShowDistrictSuggestions(false);
  };

  const removeDistrict = (index) => {
    setFormData({
      ...formData,
      allocated_districts: formData.allocated_districts.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.mobile) {
      toast.error('Name and Mobile are required');
      return;
    }
    
    if (formData.mobile.length !== 10) {
      toast.error('Mobile number must be 10 digits');
      return;
    }

    try {
      if (editingExecutive) {
        await api.put(`/admin/executives/${editingExecutive.id}`, formData);
        toast.success('Executive updated successfully');
      } else {
        const response = await api.post('/admin/executives', formData);
        setGeneratedPasscode(response.data.passcode);
        toast.success(`Executive created! Username: ${response.data.username}`);
      }
      
      setShowDialog(false);
      resetForm();
      fetchExecutives();
      fetchAllocatedDistricts();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Operation failed';
      
      // Check if it's a mobile conflict error
      if (errorMsg.includes('already used by') || errorMsg.includes('already exists')) {
        // Parse the error to extract user info
        const mobileMatch = errorMsg.match(/Mobile (\d+)/);
        const nameMatch = errorMsg.match(/used by ([^(]+)/);
        const roleMatch = errorMsg.match(/\(([^)]+)\)/);
        
        const existingName = nameMatch ? nameMatch[1].trim() : 'Unknown';
        const existingRole = roleMatch ? roleMatch[1] : 'executive';
        
        if (existingRole === 'admin') {
          toast.error(`Cannot use this mobile - it belongs to Admin: ${existingName}`);
        } else {
          // Offer to delete and recreate
          const confirmDelete = window.confirm(
            `Mobile ${formData.mobile} is already used by executive "${existingName}".\n\n` +
            `Do you want to DELETE the existing executive and create a new one with this mobile number?`
          );
          
          if (confirmDelete) {
            try {
              // Delete existing executive
              await api.delete(`/admin/executives/by-mobile/${formData.mobile}`);
              toast.success(`Existing executive "${existingName}" deleted`);
              
              // Now create new one
              const response = await api.post('/admin/executives', formData);
              setGeneratedPasscode(response.data.passcode);
              toast.success(`New executive created! Username: ${response.data.username}`);
              
              setShowDialog(false);
              resetForm();
              fetchExecutives();
              fetchAllocatedDistricts();
            } catch (deleteError) {
              toast.error(deleteError.response?.data?.detail || 'Failed to replace executive');
            }
          }
        }
      } else {
        toast.error(errorMsg);
      }
    }
  };

  const handleEdit = (executive) => {
    setEditingExecutive(executive);
    setFormData({
      name: executive.name,
      mobile: executive.mobile,
      email: executive.email || '',
      allocated_districts: executive.allocated_districts || []
    });
    setShowDialog(true);
  };

  const handleDelete = async (executive) => {
    if (window.confirm(`Are you sure you want to delete executive "${executive.name}"? This action cannot be undone.`)) {
      try {
        await api.delete(`/admin/executives/${executive.id}`);
        toast.success('Executive deleted');
        fetchExecutives();
        fetchAllocatedDistricts();
      } catch (error) {
        toast.error('Failed to delete executive');
      }
    }
  };

  const handleToggleStatus = async (executive) => {
    try {
      await api.put(`/admin/executives/${executive.id}`, { active: !executive.active });
      toast.success(`Executive ${executive.active ? 'deactivated' : 'activated'}`);
      fetchExecutives();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openCredentialsDialog = (executive) => {
    setCredentialsTarget(executive);
    setNewUsername(executive.username || '');
    setNewPassword('');
    setShowCredentialsDialog(true);
  };

  const handleResetPassword = async () => {
    if (!credentialsTarget) return;
    
    try {
      const response = await api.put(`/admin/executives/${credentialsTarget.id}/reset-password`);
      setGeneratedPasscode(response.data.new_passcode);
      toast.success('Password reset successfully');
      fetchExecutives();
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleUpdateCredentials = async () => {
    if (!credentialsTarget) return;
    
    const updates = {};
    if (newUsername && newUsername !== credentialsTarget.username) {
      updates.username = newUsername;
    }
    if (newPassword) {
      updates.password = newPassword;
    }
    
    if (Object.keys(updates).length === 0) {
      toast.error('No changes to save');
      return;
    }
    
    try {
      await api.put(`/admin/executives/${credentialsTarget.id}/update-credentials`, updates);
      toast.success('Credentials updated successfully');
      setShowCredentialsDialog(false);
      setNewPassword('');
      fetchExecutives();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update credentials');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const resetForm = () => {
    setFormData({ name: '', mobile: '', email: '', allocated_districts: [] });
    setEditingExecutive(null);
    setDistrictSearch('');
    setDistrictSuggestions([]);
  };

  return (
    <div>
      <SEO title="Executive Management | VVVSI Admin" />
      
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Executive Management</h1>
          <p className="text-slate-500 text-sm mt-1">Manage district executives with username/password access</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchExecutives} data-testid="refresh-executives-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={() => { resetForm(); setShowDialog(true); }} 
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="add-executive-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Executive
          </Button>
        </div>
      </div>

      {/* Generated Passcode Alert */}
      {generatedPasscode && (
        <Card className="mb-6 p-4 bg-green-50 border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-semibold text-green-800">New Login Credentials</p>
                <p className="text-sm text-green-700">
                  Passcode: <span className="font-mono font-bold">{generatedPasscode}</span>
                </p>
                <p className="text-xs text-green-600 mt-1">Please save these credentials. They won't be shown again.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => copyToClipboard(generatedPasscode)}>
                <Copy className="w-4 h-4 mr-1" />
                Copy
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setGeneratedPasscode('')}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Executives Table */}
      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-12 text-center font-semibold">Sl.No</TableHead>
                <TableHead className="font-semibold">Name</TableHead>
                <TableHead className="font-semibold">Username</TableHead>
                <TableHead className="font-semibold">Mobile</TableHead>
                <TableHead className="font-semibold">Email</TableHead>
                <TableHead className="font-semibold">Allocated Districts</TableHead>
                <TableHead className="text-center font-semibold">Status</TableHead>
                <TableHead className="text-center font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-2"></div>
                    <p className="text-slate-600">Loading executives...</p>
                  </TableCell>
                </TableRow>
              ) : executives.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <UserCog className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-600">No executives found</p>
                    <p className="text-slate-400 text-sm mt-1">Click "Add Executive" to create one</p>
                  </TableCell>
                </TableRow>
              ) : (
                executives.map((executive, index) => (
                  <TableRow key={executive.id} className="hover:bg-slate-50">
                    <TableCell className="text-center font-medium">{index + 1}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-teal-600" />
                        </div>
                        <span className="font-medium text-slate-900">{executive.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">
                        {executive.username || '-'}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">{executive.mobile}</TableCell>
                    <TableCell className="text-slate-600">{executive.email || '-'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {executive.allocated_districts?.length > 0 ? (
                          executive.allocated_districts.slice(0, 3).map((d, i) => (
                            <Badge key={i} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                              <MapPin className="w-3 h-3 mr-1" />
                              {d.district}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-slate-400 text-sm">No districts assigned</span>
                        )}
                        {executive.allocated_districts?.length > 3 && (
                          <Badge variant="outline" className="text-xs">+{executive.allocated_districts.length - 3} more</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Switch
                          checked={executive.active !== false}
                          onCheckedChange={() => handleToggleStatus(executive)}
                          data-testid={`toggle-executive-${executive.id}`}
                        />
                        <span className={`text-xs ${executive.active !== false ? 'text-green-600' : 'text-slate-400'}`}>
                          {executive.active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(executive)}
                          title="Edit Executive"
                          data-testid={`edit-executive-${executive.id}`}
                        >
                          <Edit2 className="w-4 h-4 text-slate-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openCredentialsDialog(executive)}
                          title="Manage Credentials"
                          data-testid={`credentials-executive-${executive.id}`}
                        >
                          <Key className="w-4 h-4 text-amber-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(executive)}
                          title="Delete Executive"
                          data-testid={`delete-executive-${executive.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Add/Edit Executive Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingExecutive ? 'Edit Executive' : 'Add New Executive'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name <span className="text-red-500">*</span></label>
              <Input
                data-testid="executive-name-input"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Full name"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Mobile <span className="text-red-500">*</span></label>
              <Input
                data-testid="executive-mobile-input"
                type="tel"
                value={formData.mobile}
                onChange={(e) => setFormData({...formData, mobile: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                placeholder="10-digit mobile number"
                required
                maxLength={10}
              />
              <p className="text-xs text-slate-500 mt-1">Used for OTP login</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <Input
                data-testid="executive-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="Email address"
              />
              <p className="text-xs text-slate-500 mt-1">Username will be generated from email or name</p>
            </div>
            
            {/* District Allocation */}
            <div>
              <label className="block text-sm font-medium mb-1">Allocated Districts</label>
              <div ref={districtInputRef} className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    data-testid="district-search-input"
                    value={districtSearch}
                    onChange={handleDistrictSearchChange}
                    onFocus={() => setShowDistrictSuggestions(true)}
                    placeholder="Search and add districts..."
                    className="pl-10"
                  />
                </div>
                
                {showDistrictSuggestions && districtSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {districtSuggestions.map((district, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => addDistrict(district)}
                        className="w-full px-4 py-2 text-left hover:bg-teal-50 flex items-center gap-2 text-sm"
                      >
                        <MapPin className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{district.district}</span>
                        <span className="text-slate-400">({district.state})</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Selected Districts */}
              {formData.allocated_districts.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {formData.allocated_districts.map((district, index) => (
                    <Badge key={index} variant="secondary" className="bg-teal-100 text-teal-800 pr-1">
                      <MapPin className="w-3 h-3 mr-1" />
                      {district.district}, {district.state}
                      <button
                        type="button"
                        onClick={() => removeDistrict(index)}
                        className="ml-2 hover:bg-teal-200 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-500 mt-2">
                Executive will only see employers and jobs from assigned districts
              </p>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700" data-testid="save-executive-btn">
                {editingExecutive ? 'Update Executive' : 'Create Executive'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credentials Management Dialog */}
      <Dialog open={showCredentialsDialog} onOpenChange={setShowCredentialsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-600" />
              Manage Credentials - {credentialsTarget?.name}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="update" className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="update">Update Credentials</TabsTrigger>
              <TabsTrigger value="reset">Reset Password</TabsTrigger>
            </TabsList>
            
            <TabsContent value="update" className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-1">Username</label>
                <Input
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="Enter username"
                />
                <p className="text-xs text-slate-500 mt-1">Only lowercase letters, numbers, and underscores</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">New Password</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Leave blank to keep current"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <Button onClick={handleUpdateCredentials} className="w-full bg-teal-600 hover:bg-teal-700">
                Save Changes
              </Button>
            </TabsContent>
            
            <TabsContent value="reset" className="space-y-4 mt-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  This will generate a new random passcode for the executive. The current password will be invalidated.
                </p>
              </div>
              
              <Button onClick={handleResetPassword} className="w-full bg-amber-600 hover:bg-amber-700">
                <Key className="w-4 h-4 mr-2" />
                Generate New Passcode
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExecutiveManagement;
